/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import { keyframes } from '@emotion/react'
import React from 'react'
import colors from '@sportninja/common/constants/appColors'
import { CATEGORIES } from '@sportninja/common/features/profile/player'

interface PlayerFormSkeletonProps {
  /** Stack vertically (mobile). Mirrors ScrollSpyLayout's `stacked`. */
  stacked?: boolean
}

// Read SECONDARY_350 (absent from the typed appColors inference) via a cast,
// the established pattern in this feature.
const palette = colors as Record<string, string>

const SECTION_COUNT = CATEGORIES.length
const FIELDS_PER_SECTION = 2

const PlayerFormSkeleton: React.FC<PlayerFormSkeletonProps> = ({
  stacked = false,
}) => {
  return (
    <div
      css={css`
        ${bodyStyle}
        flex-direction: ${stacked ? 'column' : 'row'};
      `}
      data-testid='player-form-skeleton'
      aria-hidden='true'
    >
      <div css={sidebarColStyle}>
        {CATEGORIES.map((cat) => (
          <div key={cat.key} css={sidebarItemStyle} />
        ))}
      </div>
      <div css={contentColStyle}>
        {Array.from({ length: SECTION_COUNT }).map((_, s) => (
          <div key={s} css={sectionStyle}>
            <div css={sectionHeaderBarStyle} />
            <div css={fieldRowStyle}>
              {Array.from({ length: FIELDS_PER_SECTION }).map((_, f) => (
                <div key={f} css={fieldStyle}>
                  <div css={labelBarStyle} />
                  <div css={inputBarStyle} />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default PlayerFormSkeleton

// ---- styled ----
const pulse = keyframes`
  0% { opacity: 0.5; }
  50% { opacity: 0.9; }
  100% { opacity: 0.5; }
`

const bar = css`
  border-radius: 8px;
  background-color: ${palette.SECONDARY_350};
  animation: ${pulse} 1.4s ease-in-out infinite;
`

const bodyStyle = css`
  display: flex;
  flex: 1;
  overflow: hidden;
  gap: 24px;
`

const sidebarColStyle = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 320px;
  min-width: 320px;
`

const sidebarItemStyle = css`
  ${bar}
  height: 40px;
`

const contentColStyle = css`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 32px;
  overflow: hidden;
`

const sectionStyle = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const sectionHeaderBarStyle = css`
  ${bar}
  height: 20px;
  width: 180px;
`

const fieldRowStyle = css`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

const fieldStyle = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const labelBarStyle = css`
  ${bar}
  height: 16px;
  width: 120px;
`

const inputBarStyle = css`
  ${bar}
  height: 40px;
  width: 100%;
`
